import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Database } from "@/integrations/supabase/types";
import { notifyCustomerReportReady } from "@/lib/email/notify-customer.server";
import { loadReportSettings, renderBrandedEmail } from "@/lib/email/branded.server";
import { sendEmail, escapeHtml } from "@/lib/email/resend.server";
import { kickoffAuditPipeline } from "@/lib/audit-pipeline.server";

type OrderUpdate = Database["public"]["Tables"]["orders"]["Update"];

export interface PortalOrder {
  id: string;
  order_number: string;
  status: string;
  plan_id: string;
  plan_name: string;
  website_url: string;
  email: string;
  full_name: string;
  amount_cents: number;
  currency: string;
  paid_at: string | null;
  estimated_delivery_at: string | null;
  report_url: string | null;
  created_at: string;
  audit_score: number | null;
}

export interface AdminOrder extends PortalOrder {
  company: string | null;
  industry: string | null;
  competitors: string | null;
  target_audience: string | null;
  notes: string | null;
  stripe_session_id: string | null;
  stripe_payment_intent: string | null;
  error_message: string | null;
  is_internal_audit: boolean;
  suppress_customer_email: boolean;
}

export interface AdminAuditSection {
  id: string;
  section: string;
  score: number | null;
  max_score: number | null;
  created_at: string;
}

async function assertCallerIsAdmin(callerUserId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", callerUserId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

/** Returns orders matching the signed-in user's email (case-insensitive). */
export const listMyOrders = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ orders: PortalOrder[] }> => {
    const email = (context.claims?.email as string | undefined)?.toLowerCase();
    if (!email) return { orders: [] };

    // Back-fill claimed_user_id so admin reports can link rows to a user.
    await supabaseAdmin
      .from("orders")
      .update({ claimed_user_id: context.userId })
      .ilike("email", email)
      .is("claimed_user_id", null);

    const { data, error } = await supabaseAdmin
      .from("orders")
      .select(
        "id,order_number,status,plan_id,website_url,email,full_name,amount_cents,currency,paid_at,estimated_delivery_at,report_url,created_at,audit_score, plans(name)",
      )
      .ilike("email", email)
      .neq("status", "pending_payment")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);

    return {
      orders: (data ?? []).map((row) => {
        const planRel = (row as unknown as { plans?: { name: string } | null }).plans;
        return {
          id: row.id,
          order_number: row.order_number,
          status: row.status,
          plan_id: row.plan_id,
          plan_name: planRel?.name ?? row.plan_id,
          website_url: row.website_url,
          email: row.email,
          full_name: row.full_name,
          amount_cents: row.amount_cents,
          currency: row.currency,
          paid_at: row.paid_at,
          estimated_delivery_at: row.estimated_delivery_at,
          report_url: row.report_url,
          created_at: row.created_at,
          audit_score: (row as { audit_score?: number | null }).audit_score ?? null,
        };
      }),
    };
  });

export const listAllOrders = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ orders: AdminOrder[] }> => {
    await assertCallerIsAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("orders")
      .select(
        "id,order_number,status,plan_id,website_url,email,full_name,amount_cents,currency,paid_at,estimated_delivery_at,report_url,created_at,company,industry,competitors,target_audience,notes,stripe_session_id,stripe_payment_intent,audit_score,error_message,is_internal_audit,suppress_customer_email, plans(name)",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return {
      orders: (data ?? []).map((row) => {
        const planRel = (row as unknown as { plans?: { name: string } | null }).plans;
        return {
          id: row.id,
          order_number: row.order_number,
          status: row.status,
          plan_id: row.plan_id,
          plan_name: planRel?.name ?? row.plan_id,
          website_url: row.website_url,
          email: row.email,
          full_name: row.full_name,
          amount_cents: row.amount_cents,
          currency: row.currency,
          paid_at: row.paid_at,
          estimated_delivery_at: row.estimated_delivery_at,
          report_url: row.report_url,
          created_at: row.created_at,
          company: row.company,
          industry: row.industry,
          competitors: row.competitors,
          target_audience: row.target_audience,
          notes: row.notes,
          stripe_session_id: row.stripe_session_id,
          stripe_payment_intent: row.stripe_payment_intent,
          audit_score: row.audit_score ?? null,
          error_message: row.error_message ?? null,
          is_internal_audit: (row as { is_internal_audit?: boolean | null }).is_internal_audit ?? false,
          suppress_customer_email: (row as { suppress_customer_email?: boolean | null }).suppress_customer_email ?? false,
        };
      }),
    };
  });

export const getAdminOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<{ order: AdminOrder }> => {
    await assertCallerIsAdmin(context.userId);
    const { data: row, error } = await supabaseAdmin
      .from("orders")
      .select(
        "id,order_number,status,plan_id,website_url,email,full_name,amount_cents,currency,paid_at,estimated_delivery_at,report_url,created_at,company,industry,competitors,target_audience,notes,stripe_session_id,stripe_payment_intent,audit_score,error_message,is_internal_audit,suppress_customer_email, plans(name)",
      )
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Order not found");
    const planRel = (row as unknown as { plans?: { name: string } | null }).plans;
    return {
      order: {
        id: row.id,
        order_number: row.order_number,
        status: row.status,
        plan_id: row.plan_id,
        plan_name: planRel?.name ?? row.plan_id,
        website_url: row.website_url,
        email: row.email,
        full_name: row.full_name,
        amount_cents: row.amount_cents,
        currency: row.currency,
        paid_at: row.paid_at,
        estimated_delivery_at: row.estimated_delivery_at,
        report_url: row.report_url,
        created_at: row.created_at,
        company: row.company,
        industry: row.industry,
        competitors: row.competitors,
        target_audience: row.target_audience,
        notes: row.notes,
        stripe_session_id: row.stripe_session_id,
        stripe_payment_intent: row.stripe_payment_intent,
        audit_score: row.audit_score ?? null,
        error_message: row.error_message ?? null,
        is_internal_audit: (row as { is_internal_audit?: boolean | null }).is_internal_audit ?? false,
        suppress_customer_email: (row as { suppress_customer_email?: boolean | null }).suppress_customer_email ?? false,
      },
    };
  });

const UpdateSchema = z.object({
  id: z.string().uuid(),
  status: z
    .enum(["paid", "processing", "in_review", "review_needed", "ready", "complete", "failed", "refunded"])
    .optional(),
  report_url: z
    .string()
    .trim()
    .max(1000)
    .optional()
    .or(z.literal("")),
});

export const updateOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => UpdateSchema.parse(data))
  .handler(async ({ context, data }): Promise<{ ok: true }> => {
    await assertCallerIsAdmin(context.userId);
    const patch: OrderUpdate = {};
    if (data.status !== undefined) patch.status = data.status;
    if (data.report_url !== undefined) {
      patch.report_url = data.report_url === "" ? null : data.report_url;
    }
    if (Object.keys(patch).length === 0) return { ok: true };

    // Only fire the customer "report ready" email on the transition
    // `* -> ready` AND when a report_url is present. Guard against repeat
    // edits by requiring the previous status to differ.
    const wantsReadyNotification =
      patch.status === "ready" &&
      (patch.report_url ?? undefined) !== null;

    const { data: updatedRows, error } = await supabaseAdmin
      .from("orders")
      .update(patch)
      .eq("id", data.id)
      .neq("status", "ready")
      .select(
        "order_number,email,full_name,website_url,plan_id,report_url,status,suppress_customer_email, plans(name)",
      );
    if (error) throw new Error(error.message);

    // If the row didn't match the `neq` guard (already ready), still apply
    // any other patch fields without notifying.
    if (!updatedRows || updatedRows.length === 0) {
      if (patch.status !== "ready") {
        const { error: fallbackError } = await supabaseAdmin
          .from("orders")
          .update(patch)
          .eq("id", data.id);
        if (fallbackError) throw new Error(fallbackError.message);
      } else if (data.report_url !== undefined) {
        // Already ready, just update report_url if provided.
        const { error: urlError } = await supabaseAdmin
          .from("orders")
          .update({ report_url: patch.report_url })
          .eq("id", data.id);
        if (urlError) throw new Error(urlError.message);
      }
      return { ok: true };
    }

    const row = updatedRows[0];
    const suppress =
      (row as { suppress_customer_email?: boolean | null }).suppress_customer_email === true;
    if (wantsReadyNotification && row.report_url && !suppress) {
      const planRel = (row as unknown as { plans?: { name: string } | null }).plans;
      await notifyCustomerReportReady({
        order_number: row.order_number,
        plan_name: planRel?.name ?? row.plan_id,
        full_name: row.full_name,
        email: row.email,
        website_url: row.website_url,
        report_url: row.report_url,
      });
    }
    return { ok: true };
  });

/**
 * Returns a short-lived signed URL for a customer's finished report.
 *
 * `orders.report_url` stores either:
 *   • a storage path inside the private `audit-reports` bucket
 *     (preferred, written by the run-audit Edge Function); or
 *   • a fully-qualified https URL (admin manual override / legacy rows).
 *
 * Customers may only access their own ready reports.
 */
export const getReportSignedUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<{ url: string }> => {
    const email = (context.claims?.email as string | undefined)?.toLowerCase();
    const { data: row, error } = await supabaseAdmin
      .from("orders")
      .select("id,email,status,report_url")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Order not found");

    const isOwner = email && row.email.toLowerCase() === email;
    if (!isOwner) {
      // Allow admins as well.
      const { data: roleRow } = await supabaseAdmin
        .from("user_roles")
        .select("role")
        .eq("user_id", context.userId)
        .eq("role", "admin")
        .maybeSingle();
      if (!roleRow) throw new Error("Forbidden");
    }
    if (row.status !== "ready") throw new Error("Report is not ready yet");
    if (!row.report_url) throw new Error("No report attached");

    if (/^https?:\/\//i.test(row.report_url)) {
      return { url: row.report_url };
    }

    const { data: signed, error: signErr } = await supabaseAdmin.storage
      .from("audit-reports")
      .createSignedUrl(row.report_url, 3600);
    if (signErr || !signed) {
      throw new Error(signErr?.message || "Failed to create signed URL");
    }
    // Audit log
    await supabaseAdmin.from("report_access_log").insert({
      order_id: row.id,
      source: isOwner ? "portal" : "admin",
      signed_url_expires_at: new Date(Date.now() + 3600 * 1000).toISOString(),
    });
    return { url: signed.signedUrl };
  });

// =============================================================================
// Deliver report (Step 5), admin-triggered final hand-off email for Pro/Agency
// =============================================================================

function firstName(full: string): string {
  return (full || "").split(" ")[0] || "there";
}

function grade(score: number): { grade: string; rating: string; color: string } {
  if (score >= 80) return { grade: "A", rating: "Strong GEO Presence", color: "#22C55E" };
  if (score >= 65) return { grade: "B", rating: "Fair, Room to Improve", color: "#0D7680" };
  if (score >= 50) return { grade: "C", rating: "Needs Work", color: "#F59E0B" };
  if (score >= 35) return { grade: "D", rating: "Critical Gaps, Action Required", color: "#EF4444" };
  return { grade: "F", rating: "Critical Gaps, Action Required", color: "#EF4444" };
}

export const deliverReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(
    async ({ context, data }): Promise<
      | { ok: true; alreadyDelivered: true }
      | { ok: true; alreadyDelivered: false; email_sent_to: string; expires_at: string }
    > => {
      await assertCallerIsAdmin(context.userId);

      const { data: orderRow, error: loadErr } = await supabaseAdmin
        .from("orders")
        .select(
          "id,order_number,status,plan_id,website_url,email,full_name,audit_score,report_url, plans(name)",
        )
        .eq("id", data.order_id)
        .maybeSingle();
      if (loadErr) throw new Error(loadErr.message);
      if (!orderRow) throw new Error("Order not found");

      if (orderRow.status === "complete") {
        return { ok: true, alreadyDelivered: true };
      }
      if (!["review_needed", "ready"].includes(orderRow.status)) {
        throw new Error(
          `Status is "${orderRow.status}", deliver only works from review_needed or ready.`,
        );
      }
      if (!orderRow.report_url) {
        throw new Error("No report attached, upload one first.");
      }

      // Regenerate the .docx from the latest edited audit_reports.content so
      // the customer always receives the most recent admin edits, not the
      // original LLM output. Best-effort; if the editable row doesn't exist
      // (legacy order) we skip and fall back to the existing report_url.
      await invokeRenderReport(data.order_id, "final").catch((e) =>
        console.error("[deliverReport] render-report failed (using existing report_url):", e),
      );

      // Re-load to pick up the freshly-regenerated report_url + audit_score.
      const { data: fresh } = await supabaseAdmin
        .from("orders")
        .select("report_url,audit_score")
        .eq("id", data.order_id)
        .maybeSingle();
      if (fresh?.report_url) orderRow.report_url = fresh.report_url;
      if (typeof fresh?.audit_score === "number") orderRow.audit_score = fresh.audit_score;

      const settings = await loadReportSettings();
      const ttlSeconds = Math.max(1, settings.report_link_ttl_hours) * 3600;
      const expiresAt = new Date(Date.now() + ttlSeconds * 1000).toISOString();

      let downloadUrl: string;
      if (/^https?:\/\//i.test(orderRow.report_url)) {
        downloadUrl = orderRow.report_url;
      } else {
        const { data: signed, error: signErr } = await supabaseAdmin.storage
          .from("audit-reports")
          .createSignedUrl(orderRow.report_url, ttlSeconds);
        if (signErr || !signed) {
          throw new Error(signErr?.message || "Failed to create signed URL");
        }
        downloadUrl = signed.signedUrl;
      }

      const planRel = (orderRow as unknown as { plans?: { name: string } | null }).plans;
      const planName = planRel?.name ?? orderRow.plan_id;
      const score = orderRow.audit_score ?? 0;
      const g = grade(score);
      const ttlHuman =
        settings.report_link_ttl_hours >= 24
          ? `${Math.round(settings.report_link_ttl_hours / 24)} day${settings.report_link_ttl_hours >= 48 ? "s" : ""}`
          : `${settings.report_link_ttl_hours} hour${settings.report_link_ttl_hours === 1 ? "" : "s"}`;

      const dateStr = new Date().toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });

      const GOLD = "#C8A84B";
      const body = `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.55;">Hi ${escapeHtml(firstName(orderRow.full_name))},</p>
        <p style="margin:0 0 18px;font-size:15px;line-height:1.55;">Your expert-reviewed GEO Readiness Audit for <strong>${escapeHtml(orderRow.website_url)}</strong> is ready. Our team has completed the review, added strategic commentary, and finalized your prioritized roadmap.</p>
        <div style="margin:18px 0;padding:22px;border:1px solid #eee;border-radius:12px;text-align:center;background:#fafafa;">
          <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#666;">Overall GEO Score</div>
          <div style="margin-top:8px;font-family:Georgia,serif;font-size:44px;font-weight:600;line-height:1;">${score}<span style="font-size:18px;color:#999;">/100</span></div>
          <div style="margin-top:8px;font-size:13px;color:${g.color};font-weight:600;">${g.grade}, ${escapeHtml(g.rating)}</div>
          <div style="margin-top:6px;font-size:11px;color:#999;">${escapeHtml(planName)} · Expert-reviewed · ${escapeHtml(dateStr)}</div>
        </div>
        <div style="margin:26px 0;text-align:center;"><a href="${escapeHtml(downloadUrl)}" style="display:inline-block;padding:14px 26px;background:${GOLD};color:#111;text-decoration:none;border-radius:999px;font-size:14px;font-weight:600;">Download your report (.docx)</a></div>
        <p style="margin:0 0 18px;font-size:12px;color:#888;text-align:center;">Link valid for ${escapeHtml(ttlHuman)}. Every download is logged for confidentiality.</p>
        <div style="margin:22px 0;padding:18px;border:1px solid #eee;border-radius:12px;background:#fafafa;">
          <div style="font-size:15px;font-weight:600;margin-bottom:6px;">Ready to discuss your findings?</div>
          <div style="font-size:13px;color:#555;margin-bottom:14px;">Book your complimentary 30-minute review call to walk through the report together.</div>
          <a href="${escapeHtml(settings.calendly_url)}" style="display:inline-block;padding:12px 22px;background:#111;color:#fff;text-decoration:none;border-radius:999px;font-size:13px;font-weight:600;">Book a review call (30 min, free)</a>
        </div>
        <p style="margin:24px 0 0;font-size:14px;line-height:1.6;"><strong>Where to start:</strong> open the 90-Day Roadmap section. Phase 1 actions are ordered by ROI, most clients see measurable AI citation improvement within 30 days of implementing llms.txt and Organization schema.</p>
      `;

      const html = renderBrandedEmail({
        eyebrow: `${planName} · Expert-reviewed`,
        title: `Your Pro GEO Report is ready`,
        subtitle: `${orderRow.order_number} · ${orderRow.website_url} · ${dateStr}`,
        body,
        settings,
        context: {
          client: orderRow.full_name,
          domain: orderRow.website_url,
          score,
          plan: planName,
        },
      });

      await sendEmail({
        to: orderRow.email,
        subject: `Your Pro GEO Report is ready: ${orderRow.website_url} scored ${score}/100`,
        html,
        text: `Hi ${firstName(orderRow.full_name)}, your reviewed GEO Readiness Audit for ${orderRow.website_url} is ready. Download (valid ${ttlHuman}): ${downloadUrl}\n\nBook a review call: ${settings.calendly_url}\n\n${settings.signature_name} · ${settings.signature_title}`,
        tags: [
          { name: "type", value: "deliver_report" },
          { name: "order", value: orderRow.order_number },
        ],
      });

      await supabaseAdmin.from("report_access_log").insert({
        order_id: orderRow.id,
        source: "email",
        signed_url_expires_at: expiresAt,
      });

      const { error: updErr } = await supabaseAdmin
        .from("orders")
        .update({ status: "complete" })
        .eq("id", orderRow.id);
      if (updErr) throw new Error(updErr.message);

      // Stamp the audit_reports row as published.
      await supabaseAdmin
        .from("audit_reports")
        .update({ published_at: new Date().toISOString() })
        .eq("order_id", orderRow.id);

      return {
        ok: true,
        alreadyDelivered: false,
        email_sent_to: orderRow.email,
        expires_at: expiresAt,
      };
    },
  );

// =============================================================================
// Editable report content, admin loads, edits, previews, then delivers.
// =============================================================================

async function invokeRenderReport(orderId: string, mode: "preview" | "final"): Promise<{ path: string }> {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !key) throw new Error("Missing SUPABASE_URL / service role key");
  const r = await fetch(`${url}/functions/v1/render-report`, {
    method: "POST",
    headers: { "content-type": "application/json", Authorization: `Bearer ${key}` },
    body: JSON.stringify({ order_id: orderId, mode }),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(`render-report HTTP ${r.status}: ${JSON.stringify(body)}`);
  return body as { path: string };
}

export interface AdminReportContent {
  order_id: string;
  content: JsonValue | null;
  overall_score: number | null;
  version: number;
  last_edited_at: string;
  published_at: string | null;
  exists: boolean;
}

type JsonValue =
  | string
  | number
  | boolean
  | null
  | { [k: string]: JsonValue }
  | JsonValue[];

export const getAdminReportContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<{ report: AdminReportContent }> => {
    await assertCallerIsAdmin(context.userId);
    const { data: row, error } = await supabaseAdmin
      .from("audit_reports")
      .select("order_id,content,overall_score,version,last_edited_at,published_at")
      .eq("order_id", data.order_id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) {
      return {
        report: {
          order_id: data.order_id,
          content: null,
          overall_score: null,
          version: 0,
          last_edited_at: new Date().toISOString(),
          published_at: null,
          exists: false,
        },
      };
    }
    return {
      report: {
        order_id: row.order_id,
        content: (row.content as JsonValue) ?? null,
        overall_score: row.overall_score,
        version: row.version,
        last_edited_at: row.last_edited_at,
        published_at: row.published_at,
        exists: true,
      },
    };
  });

const UpdateReportSchema = z.object({
  order_id: z.string().uuid(),
  content: z.record(z.string(), z.unknown()),
  overall_score: z.number().int().min(0).max(100).optional(),
});

export const updateAdminReportContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => UpdateReportSchema.parse(data))
  .handler(async ({ context, data }): Promise<{ ok: true; version: number }> => {
    await assertCallerIsAdmin(context.userId);
    const { data: existing } = await supabaseAdmin
      .from("audit_reports")
      .select("version")
      .eq("order_id", data.order_id)
      .maybeSingle();
    const nextVersion = (existing?.version ?? 0) + 1;
    const overall =
      typeof data.overall_score === "number"
        ? data.overall_score
        : ((data.content as any)?.overall?.score ?? null);
    const { error } = await supabaseAdmin
      .from("audit_reports")
      .upsert(
        {
          order_id: data.order_id,
          content: data.content as never,
          overall_score: overall,
          version: nextVersion,
          last_edited_at: new Date().toISOString(),
          last_edited_by: context.userId,
        },
        { onConflict: "order_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: true, version: nextVersion };
  });

export const regenerateReportPreview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<{ url: string; expires_at: string }> => {
    await assertCallerIsAdmin(context.userId);
    const { path } = await invokeRenderReport(data.order_id, "preview");
    const ttl = 3600;
    const { data: signed, error } = await supabaseAdmin.storage
      .from("audit-reports")
      .createSignedUrl(path, ttl);
    if (error || !signed) throw new Error(error?.message || "Failed to sign preview URL");
    const expires_at = new Date(Date.now() + ttl * 1000).toISOString();
    await supabaseAdmin.from("report_access_log").insert({
      order_id: data.order_id,
      source: "editor_preview",
      signed_url_expires_at: expires_at,
    });
    return { url: signed.signedUrl, expires_at };
  });

// =============================================================================
// Retry failed audits, admin-only re-trigger of the run-audit pipeline.
// =============================================================================

export const retryAudit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<{ ok: true }> => {
    await assertCallerIsAdmin(context.userId);
    const { data: row, error: loadErr } = await supabaseAdmin
      .from("orders")
      .select("id,status")
      .eq("id", data.order_id)
      .maybeSingle();
    if (loadErr) throw new Error(loadErr.message);
    if (!row) throw new Error("Order not found");
    const allowed = ["failed", "review_needed", "ready", "complete", "processing"];
    if (!allowed.includes(row.status)) {
      throw new Error(`Regenerate only works for paid orders (current: ${row.status}).`);
    }
    // Enqueue via RPC, the dispatcher runs every minute and authenticates with x-internal-secret.
    await kickoffAuditPipeline(row.id);
    return { ok: true };
  });

// =============================================================================
// Queue status, live badge data for the admin order detail page.
// =============================================================================

export interface OrderQueueStatus {
  status: string;
  started_at: string | null;
  stage: string | null;
  position: number | null;
}

export const getOrderQueueStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<OrderQueueStatus> => {
    await assertCallerIsAdmin(context.userId);

    const { data: orderRow, error: orderErr } = await supabaseAdmin
      .from("orders")
      .select("status,started_at")
      .eq("id", data.order_id)
      .maybeSingle();
    if (orderErr) throw new Error(orderErr.message);
    if (!orderRow) throw new Error("Order not found");

    const { data: queueRow } = await supabaseAdmin
      .from("audit_queue")
      .select("created_at,stage,status")
      .eq("order_id", data.order_id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    let position: number | null = null;
    if (orderRow.status === "queued" && queueRow?.created_at) {
      const { count } = await supabaseAdmin
        .from("audit_queue")
        .select("id", { count: "exact", head: true })
        .eq("status", "queued")
        .lte("created_at", queueRow.created_at);
      position = count ?? 1;
    }

    return {
      status: orderRow.status,
      started_at: orderRow.started_at ?? null,
      stage: queueRow?.stage ?? null,
      position,
    };
  });

// =============================================================================
// Admin report download, sign a URL for the .docx so admins can review the
// report from the order detail page (works for any status with a report_url,
// including review_needed for Pro). Logged to report_access_log.
// =============================================================================

export const getAdminReportDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<{ url: string; expires_at: string }> => {
    await assertCallerIsAdmin(context.userId);
    const { data: row, error } = await supabaseAdmin
      .from("orders")
      .select("id,report_url")
      .eq("id", data.order_id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Order not found");
    if (!row.report_url) throw new Error("No report attached yet");

    const ttlSeconds = 3600;
    const expiresAt = new Date(Date.now() + ttlSeconds * 1000).toISOString();

    let url: string;
    if (/^https?:\/\//i.test(row.report_url)) {
      url = row.report_url;
    } else {
      const { data: signed, error: signErr } = await supabaseAdmin.storage
        .from("audit-reports")
        .createSignedUrl(row.report_url, ttlSeconds);
      if (signErr || !signed) {
        throw new Error(signErr?.message || "Failed to create signed URL");
      }
      url = signed.signedUrl;
    }

    await supabaseAdmin.from("report_access_log").insert({
      order_id: row.id,
      source: "admin_preview",
      signed_url_expires_at: expiresAt,
    });

    return { url, expires_at: expiresAt };
  });

// =============================================================================
// Audit sections preview, admin-only.
// =============================================================================

export const getAdminAuditSections = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(
    async ({ context, data }): Promise<{ sections: AdminAuditSection[] }> => {
      await assertCallerIsAdmin(context.userId);
      const { data: rows, error } = await supabaseAdmin
        .from("audit_sections")
        .select("id,section,score,max_score,created_at")
        .eq("order_id", data.order_id)
        .order("created_at", { ascending: true });
      if (error) throw new Error(error.message);
      return {
        sections: (rows ?? []).map((r) => ({
          id: r.id,
          section: r.section,
          score: r.score,
          max_score: r.max_score,
          created_at: r.created_at,
        })),
      };
    },
  );

// =============================================================================
// Audit sections preview, customer-scoped (own orders only).
// =============================================================================

export const listMyOrderAuditSections = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(
    async ({ context, data }): Promise<{ sections: AdminAuditSection[] }> => {
      const email = (context.claims?.email as string | undefined)?.toLowerCase();
      if (!email) throw new Error("Forbidden");

      const { data: order, error: orderErr } = await supabaseAdmin
        .from("orders")
        .select("id,email")
        .eq("id", data.order_id)
        .maybeSingle();
      if (orderErr) throw new Error(orderErr.message);
      if (!order || order.email.toLowerCase() !== email) {
        throw new Error("Not found");
      }

      const { data: rows, error } = await supabaseAdmin
        .from("audit_sections")
        .select("id,section,score,max_score,created_at")
        .eq("order_id", data.order_id)
        .order("created_at", { ascending: true });
      if (error) throw new Error(error.message);
      return {
        sections: (rows ?? []).map((r) => ({
          id: r.id,
          section: r.section,
          score: r.score,
          max_score: r.max_score,
          created_at: r.created_at,
        })),
      };
    },
  );